import { useState, useEffect } from 'react';
import styles from './Events.styles';
import Pressable from '../other/Pressable';
import { can } from '../../utils/permissions';
import { fetchAPI } from '../../utils/utils';
import { isBrowser } from 'react-device-detect';
import { fetchLegalBoardBadge } from '../quotes/legal-board/api';
import { getTreasuryWarnings } from '../../utils/treasuryWarnings';

const LOCALSTORAGE_KEY = 'subvencions_seen_opening_soon';
const BADGE_STYLE = {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#e53e3e',
    color: 'white',
    borderRadius: '50%',
    minWidth: 18,
    height: 18,
    fontSize: 11,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
    lineHeight: 1,
    boxSizing: 'border-box',
};

function getOpeningSoonSlugs(grants) {
    const now = new Date();
    return grants
        .filter(g => {
            if (g.status !== 'pending' || !g.openAt) return false;
            const diff = Math.ceil((new Date(g.openAt) - now) / (1000 * 60 * 60 * 24));
            return diff > 0 && diff <= 15;
        })
        .map(g => g.slug);
}

function HeaderTabs({ userInfo, userId, treasuryWarnings }) {
    const [subvencionsBadge, setSubvencionsBadge] = useState(0);
    const [treasuryStatus, setTreasuryStatus] = useState({});
    const canAccessTreasury = can(userInfo, 'ACCESS_QUOTES');
    const treasuryUserId = userId || userInfo?.id;
    const hasTreasuryWarnings = treasuryWarnings !== undefined;
    const pendingTreasurySections = treasuryWarnings ?? getTreasuryWarnings(treasuryStatus);

    useEffect(() => {
        setTreasuryStatus({});
        if (!canAccessTreasury || hasTreasuryWarnings) return;

        let active = true;
        const updateStatus = (key, value) => {
            if (active) setTreasuryStatus(previous => ({ ...previous, [key]: value }));
        };

        fetchAPI('/quotes/payment_info', data => {
            if (data && data.success !== false) updateStatus('paymentInfo', data);
        }, false);
        fetchAPI('/stripe/connect/status', data => {
            if (data?.success) updateStatus('stripeConnectStatus', data);
        }, false);
        if (treasuryUserId) {
            fetchLegalBoardBadge(treasuryUserId)
                .then(data => {
                    if (data?.success) updateStatus('legalBoardBadge', data.badge);
                })
                .catch(() => {
                    // A failed badge request must not break navigation or hide other warnings.
                });
        }

        return () => { active = false; };
    }, [canAccessTreasury, treasuryUserId, hasTreasuryWarnings]);

    useEffect(() => {
        if (!can(userInfo, 'ACCESS_SUBVENCIONS')) return;

        fetchAPI('/subvencions/cultura', (data) => {
            if (!data || !data.items) return;

            const normalized = data.items.map(g =>
                g.status === 'pending' && !g.openAt && !g.closeAt
                    ? { ...g, status: 'waiting' }
                    : g
            );

            const openingSoon = getOpeningSoonSlugs(normalized);
            if (openingSoon.length === 0) return;

            try {
                const raw = localStorage.getItem(LOCALSTORAGE_KEY);
                const seen = raw ? JSON.parse(raw) : [];
                const seenSet = new Set(seen);
                const unseenCount = openingSoon.filter(s => !seenSet.has(s)).length;
                setSubvencionsBadge(unseenCount);
            } catch {
                setSubvencionsBadge(openingSoon.length);
            }
        }, false);
    }, [userInfo]);

    return (
        <header className='tabs' style={styles.tabs}>
            <Pressable className="tab" style={styles.tab} href='/gestio'>
                👥
                { isBrowser ? <span style={{ fontSize: 14 }}>Castellers</span> : null }
            </Pressable>
            {
                can(userInfo, 'ACCESS_CALENDAR') ? (
                    <Pressable className="tab" style={styles.tab} href='/calendar'>
                        📅
                        { isBrowser ? <span style={{ fontSize: 14 }}>Calendari</span> : null }
                    </Pressable>
                ) : null
            }
            {
                can(userInfo, 'ACCESS_NOTIFICATIONS') ? (
                    <Pressable className="tab" style={styles.tab} href='/notifications'>
                        🔔
                        { isBrowser ? <span style={{ fontSize: 14 }}>Notificacions</span> : null }
                    </Pressable>
                ) : null
            }
            {
                can(userInfo, 'ACCESS_FORMS') ? (
                    <Pressable className="tab" style={styles.tab} href='/forms'>
                        📝
                        { isBrowser ? <span style={{ fontSize: 14 }}>Formularis</span> : null }
                    </Pressable>
                ) : null
            }
            {
                can(userInfo, 'ACCESS_EDITOR') ? (
                    <Pressable className="tab" style={styles.tab} href='/editor'>
                        <span style={{ fontSize: 18, fontWeight: 'bold' }}>⸭</span>
                        { isBrowser ? <span style={{ fontSize: 14 }}>Plantilles</span> : null }
                    </Pressable>
                ) : null
            }
            <Pressable className="tab" style={styles.tab} href='/stats'>
                📊
                { isBrowser ? <span style={{ fontSize: 14 }}>Estadístiques</span> : null }
            </Pressable>
            {
                can(userInfo, 'ACCESS_MODELS') ? (
                    <Pressable className="tab" style={styles.tab} href='/models'>
                        🌟
                        { isBrowser ? <span style={{ fontSize: 14 }}>Pinyes model</span> : null }
                    </Pressable>
                ) : null
            }
            {
                can(userInfo, 'ACCESS_ETIQUETES') ? (
                    <Pressable className="tab" style={styles.tab} href='/etiquetes'>
                        🏷️
                        { isBrowser ? <span style={{ fontSize: 14 }}>Etiquetes</span> : null }
                    </Pressable>
                ) : null
            }
            {
                can(userInfo, 'ACCESS_LINKS') ? (
                    <Pressable className="tab" style={styles.tab} href='/links'>
                        🔗
                        { isBrowser ? <span style={{ fontSize: 14 }}>Enllaços</span> : null }
                    </Pressable>
                ) : null
            }
            {
                can(userInfo, 'ACCESS_SHOP') ? (
                    <Pressable className="tab" style={styles.tab} href='/botiga'>
                        🛍️
                        { isBrowser ? <span style={{ fontSize: 14 }}>Botiga</span> : null }
                    </Pressable>
                ) : null
            }
            {
                canAccessTreasury ? (
                    <Pressable className="tab" style={{ ...styles.tab, position: 'relative' }} href='/quotes'>
                        💶
                        { isBrowser ? <span style={{ fontSize: 14 }}>Tresoreria</span> : null }
                        {pendingTreasurySections.length > 0 ? (
                            <span
                                className="treasury-header-badge"
                                style={BADGE_STYLE}
                                title={`Avisos pendents: ${pendingTreasurySections.join(', ')}`}
                                aria-label="Avisos pendents a Tresoreria"
                            >
                                !
                            </span>
                        ) : null}
                    </Pressable>
                ) : null
            }
            {
                can(userInfo, 'ACCESS_SUBVENCIONS') ? (
                    <Pressable className="tab subvencions-header-tab" style={{ ...styles.tab, position: 'relative' }} href='/subvencions'>
                        🏛️
                        { isBrowser ? <span style={{ fontSize: 14 }}>Subvencions</span> : null }
                        {subvencionsBadge > 0 ? (
                            <span className="subvencions-opening-badge" style={BADGE_STYLE}>
                                {subvencionsBadge}
                            </span>
                        ) : null}
                    </Pressable>
                ) : null
            }
        </header>
    )
}

export default HeaderTabs;
